import { startCase } from 'lodash-es';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';

/**
 * Sorts profile column order by job level config order (junior→senior), then by title.
 * - Uses job_level_ulid matched against levels settings when levels are provided.
 * - Falls back to Job_Level string A–Z when levels config is empty.
 * - Profiles with no / unknown level go after those with a known level.
 * @param {string[]} profileIds
 * @param {object[]} list - Profile objects with ulid, title, job_level, job_level_ulid
 * @param {object[]} [levels=[]] - Level settings with ulid and order
 * @returns {string[]}
 */
export const sortProfileIdsByJobLevel = (profileIds, list, levels = []) => {
  const byUlid = Object.fromEntries(list.map((listProfile) => [listProfile.ulid, listProfile]));
  const compareTitle = (idA, idB) => (
    (byUlid[idA]?.title || '').localeCompare(byUlid[idB]?.title || '')
  );

  if (levels.length > 0) {
    const orderByUlid = Object.fromEntries(
      levels.map((level, index) => [level.ulid, level.order ?? index]),
    );

    return [...profileIds].sort((idA, idB) => {
      const rankA = orderByUlid[byUlid[idA]?.job_level_ulid];
      const rankB = orderByUlid[byUlid[idB]?.job_level_ulid];

      if (rankA === undefined && rankB === undefined) return compareTitle(idA, idB);
      if (rankA === undefined) return 1;
      if (rankB === undefined) return -1;
      if (rankA !== rankB) return rankA - rankB;

      return compareTitle(idA, idB);
    });
  }

  return [...profileIds].sort((idA, idB) => {
    const levelA = formatJobLevel(byUlid[idA]?.job_level, { mode: 'long' });
    const levelB = formatJobLevel(byUlid[idB]?.job_level, { mode: 'long' });

    if (!levelA && !levelB) return compareTitle(idA, idB);
    if (!levelA) return 1;
    if (!levelB) return -1;

    const levelCompare = levelA.localeCompare(levelB);

    if (levelCompare !== 0) return levelCompare;

    return compareTitle(idA, idB);
  });
};

// Builds the union of all field ids across profiles, sorted by fieldOrder or by label.
export const buildAllFieldsFromProfiles = (profilesList, fieldOrder = [], hiddenFieldKeys = []) => {
  const fieldsById = new Map();
  const hiddenFieldKeySet = new Set(hiddenFieldKeys || []);

  (profilesList || []).forEach((profile) => {
    const profileFields = profile.fields || profile.fields_json || {};

    Object.entries(profileFields).forEach(([fieldId, fieldMeta]) => {
      if (hiddenFieldKeySet.has(fieldId)) return;

      if (!fieldsById.has(fieldId)) {
        fieldsById.set(fieldId, { id: fieldId, label: startCase(fieldMeta?.label || fieldId) });
      }
    });
  });

  const fieldsList = Array.from(fieldsById.values());

  if (!Array.isArray(fieldOrder) || fieldOrder.length === 0) {
    return fieldsList.sort((fieldA, fieldB) => (
      (fieldA.label || '').localeCompare(fieldB.label || '')
    ));
  }

  const orderIndex = (fieldId) => {
    const index = fieldOrder.indexOf(fieldId);

    return index === -1 ? fieldOrder.length : index;
  };

  return fieldsList.sort((fieldA, fieldB) => {
    const orderIndexA = orderIndex(fieldA.id);
    const orderIndexB = orderIndex(fieldB.id);

    if (orderIndexA !== orderIndexB) return orderIndexA - orderIndexB;
    return (fieldA.label || '').localeCompare(fieldB.label || '');
  });
};

/**
 * Builds table rows: one row per field, each column (profile ulid) holds that profile's value.
 * @param {{ id: string, label: string }[]} allFieldsList
 * @param {object[]} profilesList
 * @returns {object[]} Row objects with fieldId, fieldLabel, and [profileUlid]: value
 */
export const buildTableRowsFromFieldsAndProfiles = (allFieldsList, profilesList) => (allFieldsList || [])
  .map((field) => {
    const row = { fieldId: field.id, fieldLabel: field.label || field.id };

    (profilesList || []).forEach((profile) => {
      const profileFields = profile.fields || profile.fields_json || {};
      const cellEntry = profileFields[field.id];

      let value = '';

      if (cellEntry && typeof cellEntry === 'object' && 'value' in cellEntry) {
        value = cellEntry.value;
      } else if (typeof cellEntry === 'string') {
        value = cellEntry;
      }

      row[profile.ulid] = value ?? '';
    });

    return row;
  });

export const isEmpty = (value) => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';

  return false;
};

/**
 * Converts HTML to plain text. When content uses <li> list items, renders each as a bullet line
 * (• item) separated by newlines for display with white-space: pre-line.
 * @param {*} value
 * @returns {string}
 */
export const toPlainText = (value) => {
  if (!value) return value;

  const stringValue = typeof value === 'string' ? value : String(value);
  const trimmed = stringValue.trim();

  if (!trimmed) return trimmed;
  if (!trimmed.includes('<')) return trimmed;

  const listItemRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  const listItems = [];
  let listItemMatch = listItemRegex.exec(trimmed);

  while (listItemMatch !== null) {
    const itemText = listItemMatch[1].replace(/<[^>]+>/g, '').trim();

    if (itemText) listItems.push(itemText);

    listItemMatch = listItemRegex.exec(trimmed);
  }

  if (listItems.length > 0) {
    return listItems.map((item) => `• ${item}`).join('\n');
  }

  return trimmed.replace(/<[^>]+>/g, '').trim();
};

export const formatCellValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' && value.trim() === '') return '—';
  return value;
};

/**
 * Builds 2D array for CSV export: [headerRow, ...metadataRows, ...dataRows].
 * @param {object[]} headerColumnList - id, header, source, jobCode, jobLevel, jobFamilyNames, employeeCountDisplay
 * @param {object[]} orderedRowsData - Row objects with fieldLabel and [col.id]: value
 * @param {(value: *) => string} toPlainTextFn
 * @returns {string[][]} Rows suitable for CSV / spreadsheet export
 */
export const buildCsvRowsForComparisonTable = (headerColumnList, orderedRowsData, toPlainTextFn) => {
  if (!headerColumnList?.length) return [];

  const headerRow = headerColumnList.map((col) => col.header);

  const metaKeys = [
    { label: 'Type', key: 'source' },
    { label: 'Job Code', key: 'jobCode' },
    { label: 'Job Level', key: 'jobLevel' },
    { label: 'Job Family', key: 'jobFamilyNames' },
    { label: 'Employees', key: 'employeeCountDisplay' },
  ];

  const formatMetaValue = (col, key) => {
    if (key === 'source') {
      return col.source === 'draft' ? 'Draft' : 'HRIS';
    }

    return col[key] || '';
  };

  const metaRows = metaKeys.map(({ label, key }) => headerColumnList.map((col) => (
    col.id === 'fieldLabel' ? label : formatMetaValue(col, key)
  )));

  const dataRows = (orderedRowsData || []).map((row) => headerColumnList.map((col) => {
    const raw = col.id === 'fieldLabel' ? row.fieldLabel : row[col.id];

    return toPlainTextFn(raw);
  }));

  return [headerRow, ...metaRows, ...dataRows];
};

// --- Graph / tree helpers (used by useArchitectureGraph and JobArchitectureCanvas)

export const sumEmployeeCountsForProfiles = (profiles) => (profiles || []).reduce(
  (sum, profile) => sum + (profile?.employee_count || 0),
  0,
);

export const getGroupTotalProfileCount = (group) => {
  const families = group?.families || [];

  return families.reduce((sum, family) => sum + (family?.profiles?.length || 0), 0);
};

export const getGroupArchitectureStats = (group) => {
  const families = group?.families || [];
  const familyCount = families.length;
  const employeeCount = families.reduce(
    (total, family) => total + sumEmployeeCountsForProfiles(family?.profiles),
    0,
  );

  return { familyCount, employeeCount };
};

export const getFamilyArchitectureStats = (family) => {
  const profiles = family?.profiles || [];

  return {
    profileCount: profiles.length,
    employeeCount: sumEmployeeCountsForProfiles(profiles),
  };
};

export const formatArchitectureFamilyCountLabel = (count) => (
  count === 1 ? '1 family' : `${count} families`
);

export const formatArchitectureProfilesCountLabel = (count) => (
  count === 1 ? '1 profile' : `${count} profiles`
);

export const formatArchitectureEmployeeCountLabel = (count) => (
  `${count} ${count === 1 ? 'employee' : 'employees'}`
);

export const formatArchitectureGroupStatsLine = (group) => {
  const { familyCount, employeeCount } = getGroupArchitectureStats(group);

  return `${formatArchitectureFamilyCountLabel(familyCount)} - ${formatArchitectureEmployeeCountLabel(employeeCount)}`;
};

export const formatArchitectureFamilyStatsLine = (family) => {
  const { profileCount, employeeCount } = getFamilyArchitectureStats(family);
  const profilePart = `${profileCount} job profile${profileCount === 1 ? '' : 's'}`;

  return `${profilePart} - ${formatArchitectureEmployeeCountLabel(employeeCount)}`;
};

export const architectureDisplayName = (name) => name || 'Uncategorized';

export const groupId = (group) => `group-${group?.ulid || 'uncategorized'}`;

export const familyNodeId = (group, family) => {
  const groupUlid = group?.ulid || 'uncategorized';
  const familyUlid = family?.ulid || 'uncategorized';

  return `family-${groupUlid}-${familyUlid}`;
};

// Group and family tree node ids that contain any of the given profile ULIDs (for expanding the list tree).
export const collectExpandedNodeIdsForProfileUlids = (treeData, profileUlids) => {
  const ulidSet = new Set((profileUlids || []).filter(Boolean));
  const expanded = new Set();

  if (ulidSet.size === 0 || !treeData?.groups?.length) {
    return expanded;
  }

  treeData.groups.forEach((group) => {
    (group.families || []).forEach((family) => {
      const profiles = family.profiles || [];
      const hasMatch = profiles.some((profile) => profile?.ulid && ulidSet.has(profile.ulid));

      if (hasMatch) {
        expanded.add(groupId(group));
        expanded.add(familyNodeId(group, family));
      }
    });
  });

  return expanded;
};

// Collect all profile ulids present in a (possibly filtered) architecture tree.
export const collectProfileUlidsFromTree = (tree) => {
  const ulids = [];

  (tree?.groups || []).forEach((group) => {
    (group.families || []).forEach((family) => {
      (family.profiles || []).forEach((profile) => {
        if (profile?.ulid) {
          ulids.push(profile.ulid);
        }
      });
    });
  });

  return ulids;
};

// Keep only selected ulids that still appear in the filtered tree; preserves order.
export const pruneSelectedProfileUlidsToVisibleTree = (selectedUlids, filteredTree) => {
  if (!selectedUlids?.length) {
    return selectedUlids || [];
  }

  const visibleUlids = new Set(collectProfileUlidsFromTree(filteredTree));

  return selectedUlids.filter((ulid) => visibleUlids.has(ulid));
};

const familyMapKeyForExpansion = (group, family) => (
  family?.ulid != null && String(family.ulid).trim() !== ''
    ? family.ulid
    : familyNodeId(group, family)
);

// Group and family tree node ids to expand when alert filters are active (expand to alert layer only).
export const collectExpandedNodeIdsForAlertFilter = (filteredTree, alertsData, matchingCodes) => {
  const expanded = new Set();

  if (!matchingCodes?.size || !filteredTree?.groups?.length) {
    return expanded;
  }

  const { byProfile, byFamily } = alertsData;
  const alertsMatch = (nodeAlerts) => (nodeAlerts || []).some((code) => matchingCodes.has(code));

  filteredTree.groups.forEach((group) => {
    (group.families || []).forEach((family) => {
      const fKey = familyMapKeyForExpansion(group, family);
      const profiles = family.profiles || [];
      const familyHasDirectAlert = alertsMatch(byFamily.get(fKey));

      const hasMatchingProfiles = profiles.some((profile) => {
        const ulid = profile?.ulid;

        return ulid && alertsMatch(byProfile.get(ulid));
      });

      if (hasMatchingProfiles) {
        expanded.add(groupId(group));
        expanded.add(familyNodeId(group, family));
      } else if (familyHasDirectAlert) {
        expanded.add(groupId(group));
      }
    });
  });

  return expanded;
};

/**
 * Groups to render: all groups, or only those with at least one family when hideEmpty is true.
 * @param {object[]} groups
 * @param {boolean} hideEmpty
 * @returns {object[]}
 */
export const getGroupsToRender = (groups, hideEmpty) => {
  if (!groups?.length) return [];
  if (!hideEmpty) return groups;

  return groups.filter((group) => (group.families?.length || 0) > 0);
};

/**
 * Finds a family in the tree by ulid.
 * @param {object} tree - Tree with groups[].families[]
 * @param {string} ulid
 * @returns {object|null}
 */
export const findFamilyByUlid = (tree, ulid) => {
  const { groups } = tree || {};

  if (!groups || ulid == null || ulid === '') return null;

  for (let index = 0; index < groups.length; index += 1) {
    const group = groups[index];
    const family = group.families?.find((candidateFamily) => String(candidateFamily.ulid) === String(ulid));

    if (family) return family;
  }

  return null;
};
